import { SHAPE_STYLES, TEXT_STYLE } from './constants';
import { COLORS } from './constants-colors';
import {
  addGameObject,
  anchoredPos,
  createScaleAnimation,
  createShapeStyleAnimation,
  createTransformAnchoredAnimation,
  createTransformSizeAnimation,
  getZeroStaticEntity,
  setParent,
} from './entity';
import {
  cloneTextStyle,
  Ease,
  hasVisibleState,
  lerpColor,
  setColorAlpha255,
  toSize,
  v2,
} from './helpers';
import {
  Alignment_Horizontal,
  EventClientToServer,
  ServerEvent_EntityProps,
  ServerEvent_TextButton,
} from './shared/api_shared_types';
import { executeCommand, setVisibilityState, setParentByEntityProps } from './simulate';
import { changeTextObjectText, createAndAddLocalTextObject } from './textobject/textobject';
import {
  AnimationType,
  Command,
  CommandType,
  Entity,
  VisibilityState,
  GameManager,
  GameObjectController,
  MoveMode,
  Shape,
  SharedObjectType,
  TextAppearStyle,
  TextButton,
  TextObject,
  TextObjectStyle,
  type ImageEnt,
  type RenderShapeStyle,
} from './types';
import { createImageEnt } from './image_entity';
import { dt } from './index';
import { createAndAddLocalContainer, setContainerStyle, setContainerStyles } from './container';
import logger from './controllers/logger';

export function controller(): GameObjectController {
  return {
    getNew: getZeroTextButton,
    sync,
    update,
  };
}

export function getZeroTextButton(ent: Entity): TextButton {
  let btn: TextButton = {
    ...ent,
    objectType: SharedObjectType.TEXT_BUTTON,
    clickAndHoldDuration: 0,
    clickAndHoldContainer: null,
    clickAndHoldTime: 0,
    executeThisFrame: false,
    container: null,
    hoverContainer: null,
    textObject: null,
    command: null,
    icon: null,
    playSFX: false,
    showLoader: false,
    hintText: '',
    hintTextObject: null,
    widthEm: 0,
    animType: '',
    onClick: null,
    textPadding: 0.8,
    padding: 0,
    paddingNoLabel: 0,
  };
  return btn;
}

export function sync(manager: GameManager, button: TextButton, props: ServerEvent_TextButton) {
  let entityProps: ServerEvent_EntityProps = {};

  if (props.hasOwnProperty('entityProps')) {
    entityProps = props.entityProps;
  }

  if (props.hasOwnProperty('showLoader')) {
    button.showLoader = props.showLoader;
  }

  if (props.hasOwnProperty('minWidthEm')) {
    button.widthEm = props.minWidthEm;
  }

  if (!button.id) {
    let style = TEXT_STYLE.BUTTON;
    let animType: '' | 'move' | 'zoom' = 'move';
    let gradient = false;

    if (entityProps.style) {
      if (entityProps.style === 'big') {
        style = TEXT_STYLE.BUTTON_BIG;
      } else if (entityProps.style === 'AI') {
        style = TEXT_STYLE.BUTTON_AI;
        animType = 'zoom';
        gradient = true;
      } else if (entityProps.style === 'leave_call') {
        style = TEXT_STYLE.BUTTON_LEAVE_CALL;
        animType = 'zoom';
      } else if (entityProps.style === 'green') {
        style = TEXT_STYLE.BUTTON_GREEN;
      }
    }

    if (entityProps.noAutoSize) {
      button.disableAutoSize = entityProps.noAutoSize;
    }
    addGameObject(manager, button);
    initTextButton(manager, button, props.label, props.iconId, style);
    button.animType = animType;
    button.playSFX = true;
    button.container.shapeStyle.gradient = gradient;
    button.container.defaultStyle.gradient = gradient;
    button.container.highlightStyle.gradient = gradient;
    button.container.shapeStyle.gradientFade = 1;
    button.container.defaultStyle.gradientFade = 1;
    button.container.highlightStyle.gradientFade = 0.7;

    setParentByEntityProps(manager, button, props.sId, props.roomId, entityProps);

    if (props.hasOwnProperty('buttonAction')) {
      const command: Command = {
        type: CommandType.ACTION,
        value: props.buttonAction,
        args: [],
      };
      if (props.buttonActionValue && props.buttonActionValue.length) {
        command.args = [props.buttonActionValue];
      }

      if (props.buttonAction.search('link')) {
        button.onClick = (event: MouseEvent) => {
          if (button.mouseOver && button.mouseDownOnMe) {
            executeCommand(command, button.manager);
          }
        };
      } else {
        button.command = command;
      }
    }
  }

  if (props.hasOwnProperty('hintText')) {
    button.hintText = props.hintText;
  }

  const stateIsVisible = hasVisibleState(button.visibilityState);
  if (props.hasOwnProperty('showButton')) {
    if (props.showButton && !stateIsVisible) {
      setVisibilityState(button, VisibilityState.APPEARING);
      // entityMoveToFront( ent );
    } else if (!props.showButton && stateIsVisible) {
      setVisibilityState(button, VisibilityState.HIDING);
    }
  } else if (!stateIsVisible) {
    setVisibilityState(button, VisibilityState.APPEARING);
  }

  // setGameObjectyEntityProps(textButton, entityProps);
}

function initTextButton(
  manager: GameManager,
  button: TextButton,
  label: string,
  iconId = '',
  style: TextObjectStyle = null,
  textWidthEm: number = 2,
  clickAndHoldDuration: number = 0,
): TextObject {
  button.shape = Shape.RECT;
  button.clickable = true;
  button.allowHighlight = true;
  button.allowDrag = false;

  if (!style) {
    style = TEXT_STYLE.BUTTON;
  }

  if (!iconId) {
    style.textAlign = Alignment_Horizontal.CENTER;
  }

  if (!button.widthEm) {
    button.widthEm = 0.5;
  }

  style = cloneTextStyle(style);

  if (button.disableAutoSize) {
    style.doAutoResize = false;
  }

  {
    let c = createAndAddLocalContainer(manager, getZeroStaticEntity());
    setContainerStyles(
      c,
      {
        fillColor: style.color,
        strokeWeight: 0,
        strokeColor: COLORS.BLACK_100,
        lineDash: [],
        lineDashOffset: 0,
        gradient: false,
        shadow: null,
      },
      {
        fillColor: style.colorHighlight,
        strokeWeight: 0,
        strokeColor: COLORS.BLACK_100,
        lineDash: [],
        lineDashOffset: 0,
        gradient: false,
        shadow: null,
      },
    );
    setParent(c, button, v2());
    setVisibilityState(c, VisibilityState.DEFAULT);
    button.container = c;
  }

  if (iconId) {
    const props: Partial<ImageEnt> = {
      moveMode: MoveMode.ANCHORED,
      anchoredPos: anchoredPos(v2(1, 0.5), v2(-14, 0), v2(1, 0.5)),
      allowHighlight: false,
      maxWidthEm: style.fontSizeEm / 3.5,
      maxHeightEm: style.fontSizeEm / 3.5,
    };

    let icon = createImageEnt(
      manager,
      getZeroStaticEntity({ disableAutoSize: button.disableAutoSize }),
      iconId,
      props,
    );

    icon.moveMode = MoveMode.ANCHORED;
    icon.anchoredPos = anchoredPos(v2(1, 0.5), v2(-14, 0), v2(1, 0.5));
    button.icon = icon;
  }

  const pixelsPerUnit = button.disableAutoSize ? 100 : manager.rootUnitSizePx;
  const ICON_SPACING_EM = 0.1;
  const ICON_WIDTH_EM = iconId ? style.fontSizeEm / 3 + ICON_SPACING_EM : 0;

  //default textpadding
  if (!button.textPadding) button.textPadding = 0.8;
  const PADDING_EM = button.textPadding * style.fontSizeEm;

  let tObj = createAndAddLocalTextObject(
    manager,
    getZeroStaticEntity(),
    label,
    { ...style, color: COLORS.TRANSPARENT, colorHighlight: COLORS.TRANSPARENT },
    textWidthEm,
    false,
  );

  tObj.onlyShowInDefaultState = false;
  tObj.appearStyle = TextAppearStyle.FADE_AND_MOVE_RIGHT;
  tObj.clickable = true;
  tObj.passClick = true;
  tObj.allowHighlight = true;
  tObj.passHover = true;

  button.textObject = tObj;
  button.textObject.moveMode = MoveMode.ANCHORED;
  if (!iconId) {
    button.textObject.anchoredPos = anchoredPos(v2(0.5, 0.5), v2(0, 0), v2(0.5, 0.5));
  } else {
    button.textObject.anchoredPos = anchoredPos(v2(0, 0.5), v2(20, 0), v2(0, 0.5));
  }

  button.padding = (PADDING_EM + ICON_WIDTH_EM) * pixelsPerUnit;
  button.paddingNoLabel = ICON_WIDTH_EM * pixelsPerUnit;

  const size = button.textObject.text.length
    ? button.textObject.width + button.padding
    : button.paddingNoLabel;
  button.width = Math.max(size, button.widthEm * pixelsPerUnit);

  if (button.icon) {
    setParent(button.icon, button);
  }
  setParent(button.textObject, button);

  button.height = button.textObject.height;
  button.container.width = button.width;
  button.container.height = button.height;
  button.container.cornerRadius = style.cornerRadius[0];

  if (button.hoverContainer) {
    button.hoverContainer.width = button.width;
    button.hoverContainer.height = button.height;
    button.hoverContainer.cornerRadius = style.cornerRadius[0];
  }

  if (clickAndHoldDuration) {
    button.clickAndHoldDuration = clickAndHoldDuration;
  }

  return tObj;
}

export function setTextbuttonStyle(
  btn: TextButton,
  style: RenderShapeStyle,
  styleHover: RenderShapeStyle,
) {
  setContainerStyles(btn.container, style, styleHover);
}

export function createAndAddLocalTextButton(
  manager: GameManager,
  ent: Entity,
  title: string,
  iconId: string = '',
  style: TextObjectStyle = null,
  widthEm: number = 0,
  textWidthEm: number = 2,
  clickAndHoldDuration: number = 0,
  textPadding = 0.8,
): TextButton {
  let textButton = getZeroTextButton(ent);
  textButton.widthEm = widthEm;
  textButton.textPadding = textPadding;
  addGameObject(manager, textButton);

  initTextButton(manager, textButton, title, iconId, style, textWidthEm, clickAndHoldDuration);

  return textButton;
}

function update(button: TextButton): boolean {
  let isDirty = false;
  const state = button.visibilityState;
  const progress = button.stateProgress;

  button.executeThisFrame = false;

  if (button.manager.space.layoutChanged && !button.disableAutoSize) {
    const pixelsPerUnit = button.disableAutoSize ? 100 : button.manager.rootUnitSizePx;

    if (!button.icon) {
      button.textObject.anchoredPos = anchoredPos(v2(0.5, 0.5), v2(0, 0), v2(0.5, 0.5));
    } else {
      button.textObject.anchoredPos = anchoredPos(v2(0, 0.5), v2(20, 0), v2(0, 0.5));
    }

    const size = button.textObject.text.length
      ? button.textObject.width + button.padding
      : button.paddingNoLabel;
    button.width = Math.max(size, button.widthEm * pixelsPerUnit);

    button.height = button.textObject.height;
    button.container.width = button.width;
    button.container.height = button.height;
    if (button.hoverContainer) {
      button.hoverContainer.width = button.width;
      button.hoverContainer.height = button.height;
    }
  }

  if (state === VisibilityState.APPEARING) {
    button.container.defaultStyle.fillColor = lerpColor(
      setColorAlpha255(button.container.shapeStyle.fillColor, 0),
      button.container.shapeStyle.fillColor,
      progress,
    );
  } else if (state === VisibilityState.DISAPPEARING || state === VisibilityState.HIDING) {
    button.container.defaultStyle.fillColor = lerpColor(
      button.container.shapeStyle.fillColor,
      setColorAlpha255(button.container.shapeStyle.fillColor, 0),
      progress,
    );
  } else if (button.entityStateChanged) {
    if (state === VisibilityState.DEFAULT) {
      button.container.defaultStyle.fillColor = lerpColor(
        setColorAlpha255(button.container.shapeStyle.fillColor, 0),
        button.container.shapeStyle.fillColor,
        1,
      );
    }
    if (state === VisibilityState.HIDDEN) {
      button.container.defaultStyle.fillColor = lerpColor(
        setColorAlpha255(button.container.shapeStyle.fillColor, 0),
        button.container.shapeStyle.fillColor,
        0,
      );
    }
  }

  let execute = false;
  if (button.clickedThisFrame && button.clickAndHoldDuration === 0) {
    execute = true;
  }

  if (
    button.clickAndHoldDuration &&
    button.mouseDownOnMe &&
    button.mouseOver &&
    button.clickAndHoldTime >= 0
  ) {
    if (!button.clickAndHoldContainer) {
      button.clickAndHoldContainer = createAndAddLocalContainer(
        button.manager,
        getZeroStaticEntity(),
      );
      setContainerStyle(button.clickAndHoldContainer, SHAPE_STYLES.BUTTON_HOLD_RED);
      button.clickAndHoldContainer.moveMode = MoveMode.ANCHORED;
      button.clickAndHoldContainer.anchoredPos = anchoredPos(v2(0, 0.5), v2(0, 0), v2(0, 0.5));
      setParent(button.clickAndHoldContainer, button.container);
      button.clickAndHoldContainer.height = button.container.height;
    }

    button.clickAndHoldTime += dt;

    isDirty = true;

    const p = button.clickAndHoldTime / button.clickAndHoldDuration;

    if (button.clickAndHoldContainer) {
      button.clickAndHoldContainer.width = p * button.container.width;
    }

    if (button.clickAndHoldTime >= button.clickAndHoldDuration) {
      execute = true;
      button.clickAndHoldTime = -1;
      if (button.clickAndHoldContainer) {
        setVisibilityState(button.clickAndHoldContainer, VisibilityState.DISAPPEARING);
        button.clickAndHoldContainer = null;
      }
    }
  } else if (button.clickAndHoldTime !== 0 && !button.mouseDownOnMe) {
    button.clickAndHoldTime = 0;
    if (button.clickAndHoldContainer) {
      setVisibilityState(button.clickAndHoldContainer, VisibilityState.DISAPPEARING);
      button.clickAndHoldContainer = null;
    }
  }

  if (execute) {
    button.executeThisFrame = true;
    if (button.command) {
      executeCommand(button.command, button.manager);
    }

    if (button.showLoader) {
      if (!button.icon) {
        let icon = createImageEnt(
          button.manager,
          getZeroStaticEntity({ disableAutoSize: button.disableAutoSize }),
          'thnkLogoWhiteSmall',
        );
        button.icon = icon;
        icon.moveMode = MoveMode.ANCHORED;
        icon.anchoredPos = anchoredPos(v2(0.5, 0.5), v2(0, 0), v2(0.5, 0.5));
        icon.allowHighlight = false;
        const pixelsPerUnit = button.disableAutoSize ? 100 : button.manager.rootUnitSizePx;
        icon.maxWidthEm = (Math.min(50, button.height) / pixelsPerUnit) * 0.65;
        icon.maxHeightEm = icon.maxWidthEm;
        setParent(icon, button);
        setVisibilityState(button.textObject, VisibilityState.HIDING);
      } else {
        button.icon.requestNewImageId = 'thnkLogoWhiteSmall';
      }
      button.icon.rotationSpeed = 1;
      button.clickable = false;
      button.allowHighlight = false;
    }
  }

  if (button.clickable && button.startHighlightThisFrame) {
    hover(button);
  } else if (button.clickable && button.stopHighlightThisFrame) {
    stopHover(button);
  }

  return isDirty;
}

function hover(button: TextButton) {
  const state = button.visibilityState;
  if (state === VisibilityState.DEFAULT) {
    createShapeStyleAnimation(button.manager, button.container, {
      type: AnimationType.SHAPE_STYLE,
      end: button.container.highlightStyle,
      duration: 0.2,
      easeFunction: Ease.inOutQuad,
    });
  }

  if (button.icon) {
    if (button.animType === '' || button.animType === 'move') {
      createTransformAnchoredAnimation(button.manager, button.icon, {
        type: AnimationType.ANCHORED,
        end: anchoredPos(v2(1, 0.5), v2(-6, 0), v2(1, 0.5)),
        duration: 0.2,
        easeFunction: Ease.inOutQuad,
      });
    } else if (button.animType === 'zoom') {
      createScaleAnimation(button.manager, button.icon, {
        type: AnimationType.SCALE,
        end: 1.5,
        duration: 0.2,
        easeFunction: Ease.inOutQuad,
      });
    }
  }

  if (button.hoverContainer) {
    const newSize = toSize(button.width + 5, button.height + 5);
    createTransformSizeAnimation(button.manager, button.hoverContainer, {
      type: AnimationType.TRANSFORM_SIZE,
      end: { sizePx: newSize, sizeEm: null },
      duration: 0.2,
      easeFunction: Ease.outBack,
    });
  }
}

function stopHover(button: TextButton) {
  const state = button.visibilityState;
  if (state === VisibilityState.DEFAULT) {
    createShapeStyleAnimation(button.manager, button.container, {
      type: AnimationType.SHAPE_STYLE,
      end: button.container.defaultStyle,
      duration: 0.2,
      easeFunction: Ease.inOutQuad,
    });
  }

  if (button.icon) {
    if (button.animType === '' || button.animType === 'move') {
      createTransformAnchoredAnimation(button.manager, button.icon, {
        type: AnimationType.ANCHORED,
        end: anchoredPos(v2(1, 0.5), v2(-14, 0), v2(1, 0.5)),
        duration: 0.2,
        easeFunction: Ease.inOutQuad,
      });
    } else if (button.animType === 'zoom') {
      createScaleAnimation(button.manager, button.icon, {
        type: AnimationType.SCALE,
        end: 1,
        duration: 0.2,
        easeFunction: Ease.inOutQuad,
      });
    }
  }

  if (button.hoverContainer) {
    const newSize = toSize(button.width, button.height);
    createTransformSizeAnimation(button.manager, button.hoverContainer, {
      type: AnimationType.TRANSFORM_SIZE,
      end: { sizePx: newSize, sizeEm: null },
      duration: 0.2,
      easeFunction: Ease.inOutQuad,
    });
  }
}

export function setTextButtonLabel(button: TextButton, label: string) {
  if (label !== button.textObject.text) {
    changeTextObjectText(button.textObject, label);

    const pixelsPerUnit = button.disableAutoSize ? 100 : button.manager.rootUnitSizePx;

    const size = button.textObject.text.length
      ? button.textObject.width + button.padding
      : button.paddingNoLabel;
    button.width = Math.max(size, button.widthEm * pixelsPerUnit);
    button.height = button.textObject.height;
    button.container.width = button.width;
    button.container.height = button.height;
    if (button.hoverContainer) {
      button.hoverContainer.width = button.width;
      button.hoverContainer.height = button.height;
    }

    button.layoutChanged = true;
  }
}
